import type {
  BoolExpr,
  BoolExprGroup,
  DateTimeSetExpr,
  VariableValue,
} from '@/proto/logiblocks/logiblocks';
import { ComparisonOp } from '@/proto/logiblocks/logiblocks';
import { VariableType } from '@/proto/common';
import type { GraphData } from '../../types';
import { validateVariableRef, type ValidationError } from '../../validation';
import { getVariableType } from '@/pages/Logiblocks/variables/variableUtils';
import { COMPARISON_OP_LABELS } from '../../enumData';
import { getExprLhs } from './expressionOp';

/** Result of validating part of a BoolExprGroup tree. */
export interface ValidationResult {
  errors: ValidationError[];
  /** Number of leaf (non-group) expressions visited, used for condition numbering. */
  leafCount: number;
}

/**
 * Whether a VariableValue has its underlying value field populated.
 * A cleared number input can produce a VariableValue with a type set but
 * no corresponding oneof populated (e.g. number_value: undefined), which is
 * invalid for execution.
 */
export function isLiteralComplete(v: VariableValue): boolean {
  return (
    v.string_value != null ||
    v.number_value != null ||
    v.bool_value != null ||
    v.datetime_value != null ||
    v.labeled_id_value != null ||
    v.labeled_id_list_value != null
  );
}

/** Whether a DateTimeSetExpr has something to evaluate: a preset, a saved
 * list, or at least one inline date/day. */
function hasDateTimeSetSource(expr: DateTimeSetExpr): boolean {
  return (
    !!expr.system_preset_id ||
    !!expr.saved_list_id ||
    (expr.specific_dates?.dates.length ?? 0) > 0 ||
    (expr.days_of_week?.days.length ?? 0) > 0
  );
}

/** Operators that require no RHS value (unary checks). */
const NO_RHS_OPS = new Set<ComparisonOp>([
  ComparisonOp.COMPARISON_EXISTS,
  ComparisonOp.COMPARISON_NOT_EXISTS,
  ComparisonOp.COMPARISON_IS_EMPTY,
  ComparisonOp.COMPARISON_IS_NOT_EMPTY,
  ComparisonOp.COMPARISON_IS_TRUE,
  ComparisonOp.COMPARISON_IS_FALSE,
]);

/** Operators that take a list of values rather than a single RHS literal. */
const LIST_RHS_OPS = new Set<ComparisonOp>([
  ComparisonOp.COMPARISON_IN_LIST,
  ComparisonOp.COMPARISON_NOT_IN_LIST,
  ComparisonOp.COMPARISON_CONTAINS,
  ComparisonOp.COMPARISON_NOT_CONTAINS,
]);

/**
 * Operators that are ONLY ever a list RHS — unlike CONTAINS/NOT_CONTAINS
 * (also in LIST_RHS_OPS above, for the UI's list-vs-literal input choice),
 * IN_LIST/NOT_IN_LIST never accept a literal or variable RHS.
 */
const STRICT_LIST_ONLY_OPS = new Set<ComparisonOp>([
  ComparisonOp.COMPARISON_IN_LIST,
  ComparisonOp.COMPARISON_NOT_IN_LIST,
]);

/** Operators that require a second RHS value (upper bound). */
const DUAL_RHS_OPS = new Set<ComparisonOp>([
  ComparisonOp.COMPARISON_BETWEEN,
  ComparisonOp.COMPARISON_NOT_BETWEEN,
]);

/**
 * Whether a comparison operator requires a right-hand-side value input.
 * Returns false for unary operators (EXISTS, IS_EMPTY, IS_TRUE, etc.).
 */
export function comparisonNeedsRhs(op: ComparisonOp): boolean {
  return !NO_RHS_OPS.has(op);
}

/** Whether a comparison operator takes a list RHS (IN_LIST / NOT_IN_LIST). */
export function comparisonIsListRhs(op: ComparisonOp): boolean {
  return LIST_RHS_OPS.has(op);
}

/** Whether a comparison operator requires a second RHS bound (BETWEEN). */
export function comparisonNeedsRhs2(op: ComparisonOp): boolean {
  return DUAL_RHS_OPS.has(op);
}

/**
 * Validate a single leaf BoolExpr: checks that required fields are present
 * and required RHS values are filled in. Does not validate recurring window
 * time ordering — startSeconds > endSeconds is valid for overnight UTC windows.
 * Does not verify that VariableRefs resolve in the current graph —
 * validateGroupRefs handles that.
 *
 * @param index - zero-based condition number used to form the "Condition N" label
 */
export function validateLeafStructure(
  expr: BoolExpr,
  index: number
): ValidationResult {
  const errors: ValidationError[] = [];
  const label = `Condition ${index + 1}`;

  if (expr.comparison_expr) {
    const c = expr.comparison_expr;
    if (!c.variable) {
      errors.push({
        type: 'REQUIRED_FIELD',
        field: label,
        message: 'Variable is required',
      });
    }
    // CONTAINS / NOT_CONTAINS take either shape: a list (multi-select "one
    // of these values") or a literal (free-text substring match on a
    // company-defined enum field) — see needsFreeTextRhs in expressionOp.ts.
    // IN_LIST / NOT_IN_LIST are still list-only, so a literal/variable RHS
    // doesn't satisfy them.
    const hasListRhs = (c.rhs?.list?.items?.length ?? 0) > 0;
    const hasLiteralOrVariableRhs =
      !!(c.rhs?.literal && isLiteralComplete(c.rhs.literal)) ||
      !!c.rhs?.variable;
    const hasRhs = STRICT_LIST_ONLY_OPS.has(c.operator)
      ? hasListRhs
      : hasListRhs || hasLiteralOrVariableRhs;
    if (comparisonNeedsRhs(c.operator) && !hasRhs) {
      errors.push({
        type: 'REQUIRED_FIELD',
        field: label,
        message: 'A comparison value is required',
      });
    }
    // Labeled id values hold a run's values. Comparison literals can't use them. Literals sit
    // in rhs, in rhs2 (the BETWEEN upper bound) and in a list RHS.
    const literals: VariableValue[] = [];
    for (const rhs of [c.rhs, c.rhs2]) {
      if (rhs?.literal) {
        literals.push(rhs.literal);
      }
      if (rhs?.list) {
        literals.push(...rhs.list.items);
      }
    }
    for (const literal of literals) {
      // Our frontend doesn't actually allow the construction of labeledId literals, so
      // in practice nobody should ever see this error.
      if (
        literal.labeled_id_value != null ||
        literal.labeled_id_list_value != null
      ) {
        errors.push({
          type: 'VALUE_ERROR',
          field: label,
          message: "A condition's comparison value can't be a labeled ID.",
        });
      }
    }
  } else if (expr.recurring_window_expr || expr.date_time_set_expr) {
    if (!getExprLhs(expr)) {
      errors.push({
        type: 'REQUIRED_FIELD',
        field: label,
        message: 'Variable is required',
      });
    }
    if (
      expr.date_time_set_expr &&
      !hasDateTimeSetSource(expr.date_time_set_expr)
    ) {
      errors.push({
        type: 'REQUIRED_FIELD',
        field: label,
        message: 'A date or day selection is required',
      });
    }
  }

  return { errors, leafCount: 1 };
}

/**
 * Collect structural validation errors for a BoolExprGroup tree — presence
 * and shape. An empty root group is allowed ("always
 * passes"); empty nested groups are not. Does not check whether VariableRefs
 * resolve in the current graph — pair with validateGroupRefs for that.
 *
 * @param baseIndex - starting condition number (0 for the root call); recursive
 *                    calls pass a running counter so nested leaves get unique
 *                    "Condition N" labels
 */
export function validateGroupStructure(
  group: BoolExprGroup,
  baseIndex: number,
  isRoot = false
): ValidationResult {
  if (group.expressions.length === 0) {
    if (isRoot) {
      return { errors: [], leafCount: 0 };
    }
    return {
      errors: [
        {
          type: 'REQUIRED_FIELD',
          field: 'Conditions',
          message: 'A condition group must not be empty',
        },
      ],
      leafCount: 0,
    };
  }
  const errors: ValidationError[] = [];
  let leafCounter = baseIndex;
  for (const expr of group.expressions) {
    if (expr.group) {
      const result = validateGroupStructure(expr.group, leafCounter);
      errors.push(...result.errors);
      leafCounter += result.leafCount;
    } else {
      const result = validateLeafStructure(expr, leafCounter);
      errors.push(...result.errors);
      leafCounter += result.leafCount;
    }
  }
  return { errors, leafCount: leafCounter - baseIndex };
}

/**
 * Verify every present VariableRef in a BoolExprGroup tree resolves to a
 * valid ancestor or system variable for the given node. Pairs with
 * validateGroupStructure — they share the same "Condition N" labeling by
 * threading the same running leafCounter.
 */
export function validateGroupRefs(
  group: BoolExprGroup,
  baseIndex: number,
  nodeId: string,
  graph: GraphData
): ValidationResult {
  const errors: ValidationError[] = [];
  let leafCounter = baseIndex;

  for (const expr of group.expressions) {
    if (expr.group) {
      const result = validateGroupRefs(expr.group, leafCounter, nodeId, graph);
      errors.push(...result.errors);
      leafCounter += result.leafCount;
      continue;
    }

    const label = `Condition ${leafCounter + 1}`;
    const lhs = getExprLhs(expr);
    if (lhs) {
      const err = validateVariableRef(lhs, nodeId, graph, label);
      if (err) errors.push(err);
    }
    const rhsVariable = expr.comparison_expr?.rhs?.variable;
    if (rhsVariable) {
      const err = validateVariableRef(rhsVariable, nodeId, graph, label);
      if (err) errors.push(err);
    }
    leafCounter += 1;
  }

  return { errors, leafCount: leafCounter - baseIndex };
}

/**
 * Comparison operators every variable type supports.
 */
const UNIVERSAL_OPS: ComparisonOp[] = [
  ComparisonOp.COMPARISON_EQUALS,
  ComparisonOp.COMPARISON_NOT_EQUALS,
  ComparisonOp.COMPARISON_EXISTS,
  ComparisonOp.COMPARISON_NOT_EXISTS,
];

/**
 * Comparison operators allowed for each variable type. Mirrors the backend
 * catalog in `backend/logiblocks/graph/operator_catalog.py`; keep the two in
 * sync. This is the lenient superset of every operator the editor has ever
 * offered for a type, so validation never rejects a config the dropdown could
 * build. Between / Not Between are intentionally absent for DATE_TIME (date
 * ranges use the Within / Not Within recurring window instead) but stay valid
 * for NUMBER.
 */
export const OPERATOR_CATALOG: Partial<
  Record<VariableType, Set<ComparisonOp>>
> = {
  [VariableType.STRING]: new Set([
    ...UNIVERSAL_OPS,
    ComparisonOp.COMPARISON_CONTAINS,
    ComparisonOp.COMPARISON_NOT_CONTAINS,
    ComparisonOp.COMPARISON_STARTS_WITH,
    ComparisonOp.COMPARISON_ENDS_WITH,
    ComparisonOp.COMPARISON_IS_EMPTY,
    ComparisonOp.COMPARISON_IS_NOT_EMPTY,
    ComparisonOp.COMPARISON_REGEX_MATCHES,
  ]),
  [VariableType.TEXT]: new Set([
    ...UNIVERSAL_OPS,
    ComparisonOp.COMPARISON_CONTAINS,
    ComparisonOp.COMPARISON_NOT_CONTAINS,
    ComparisonOp.COMPARISON_STARTS_WITH,
    ComparisonOp.COMPARISON_ENDS_WITH,
    ComparisonOp.COMPARISON_IS_EMPTY,
    ComparisonOp.COMPARISON_IS_NOT_EMPTY,
    ComparisonOp.COMPARISON_REGEX_MATCHES,
  ]),
  [VariableType.NUMBER]: new Set([
    ...UNIVERSAL_OPS,
    ComparisonOp.COMPARISON_GREATER_THAN,
    ComparisonOp.COMPARISON_GREATER_THAN_OR_EQUAL,
    ComparisonOp.COMPARISON_LESS_THAN,
    ComparisonOp.COMPARISON_LESS_THAN_OR_EQUAL,
    ComparisonOp.COMPARISON_BETWEEN,
    ComparisonOp.COMPARISON_NOT_BETWEEN,
  ]),
  [VariableType.BOOL]: new Set([
    ...UNIVERSAL_OPS,
    ComparisonOp.COMPARISON_IS_TRUE,
    ComparisonOp.COMPARISON_IS_FALSE,
  ]),
  [VariableType.DATE_TIME]: new Set([
    ...UNIVERSAL_OPS,
    ComparisonOp.COMPARISON_BEFORE,
    ComparisonOp.COMPARISON_ON_OR_BEFORE,
    ComparisonOp.COMPARISON_AFTER,
    ComparisonOp.COMPARISON_ON_OR_AFTER,
  ]),
  [VariableType.ALERT_TYPE]: new Set([
    ...UNIVERSAL_OPS,
    ComparisonOp.COMPARISON_IN_LIST,
    ComparisonOp.COMPARISON_NOT_IN_LIST,
  ]),
  [VariableType.SHIPMENT]: new Set(UNIVERSAL_OPS),
  [VariableType.ALERT]: new Set(UNIVERSAL_OPS),
  [VariableType.TRANSPORT_PROVIDER]: new Set([
    ...UNIVERSAL_OPS,
    ComparisonOp.COMPARISON_CONTAINS,
    ComparisonOp.COMPARISON_NOT_CONTAINS,
  ]),
  [VariableType.DATE_TIME_RANGE]: new Set([
    ...UNIVERSAL_OPS,
    ComparisonOp.COMPARISON_BEFORE,
    ComparisonOp.COMPARISON_ON_OR_BEFORE,
    ComparisonOp.COMPARISON_AFTER,
    ComparisonOp.COMPARISON_ON_OR_AFTER,
  ]),
  // A value picked from a fixed list can only be compared against another value in that list.
  [VariableType.SENTIMENT]: new Set(UNIVERSAL_OPS),
  [VariableType.ETA_DIRECTION]: new Set(UNIVERSAL_OPS),
  [VariableType.ALERT_STATUS]: new Set(UNIVERSAL_OPS),
  [VariableType.INBOUND_EVENT_TYPE]: new Set(UNIVERSAL_OPS),
  [VariableType.ACTION_TAKEN]: new Set(UNIVERSAL_OPS),
};

/** Within / Not Within leaves (a date-time set or a legacy recurring window) are valid for DATE_TIME and DATE_TIME_RANGE. */
export const RECURRING_WINDOW_TYPES: Set<VariableType> = new Set([
  VariableType.DATE_TIME,
  VariableType.DATE_TIME_RANGE,
]);

/**
 * Operator-catalog check for a single leaf: a comparison operator must be
 * allowed for its variable's type, and a Within / Not Within leaf is valid only for a
 * date-time variable. Returns undefined when the leaf is fine or its type can't
 * be resolved (the backend skips unknown types too).
 */
function validateLeafOperator(
  expr: BoolExpr,
  label: string,
  graph: GraphData
): ValidationError | undefined {
  const ref = getExprLhs(expr);
  if (!ref) {
    return undefined;
  }
  const varType = getVariableType(ref, graph.nodes);
  if (varType === undefined) {
    return undefined;
  }

  if (expr.comparison_expr) {
    const op = expr.comparison_expr.operator;
    const allowed = OPERATOR_CATALOG[varType];
    if (allowed && !allowed.has(op)) {
      return {
        type: 'VALUE_ERROR',
        field: label,
        message: `${COMPARISON_OP_LABELS[op]} is not a valid operator for ${VariableType[varType]} variables.`,
      };
    }
    return undefined;
  }

  if (
    (expr.recurring_window_expr || expr.date_time_set_expr) &&
    !RECURRING_WINDOW_TYPES.has(varType)
  ) {
    return {
      type: 'VALUE_ERROR',
      field: label,
      message: `Within / Not Within is not a valid operator for ${VariableType[varType]} variables.`,
    };
  }

  return undefined;
}

/**
 * Verify every leaf's operator is valid for its variable's type, mirroring the
 * backend operator catalog. Frontend and backend validate independently, so
 * this runs regardless of what the dropdown currently offers. Shares the
 * running leafCounter with validateGroupStructure / validateGroupRefs so nested
 * leaves get the same "Condition N" labels.
 */
export function validateOperatorCatalog(
  group: BoolExprGroup,
  baseIndex: number,
  graph: GraphData
): ValidationResult {
  const errors: ValidationError[] = [];
  let leafCounter = baseIndex;

  for (const expr of group.expressions) {
    if (expr.group) {
      const result = validateOperatorCatalog(expr.group, leafCounter, graph);
      errors.push(...result.errors);
      leafCounter += result.leafCount;
      continue;
    }

    const label = `Condition ${leafCounter + 1}`;
    const error = validateLeafOperator(expr, label, graph);
    if (error) {
      errors.push(error);
    }
    leafCounter += 1;
  }

  return { errors, leafCount: leafCounter - baseIndex };
}
